import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getLocale, shallowEqual, useIntl, useSelector } from 'umi';
import { LANGUAGE_EN } from '@/pages/dashboard/_config/constant';
import classNames from 'classnames';
import SvgIcon from '@home/components/SvgIcon';
import { getName } from '@home/utils/common';
import Abnormal from '@home/components/Abnormal';
import CommonBubble from '@home/components/CommonBubble';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import {
  currentGameRender,
  getGameHeader,
  getMetricRender,
  getTop10MidianHeader,
} from './getRender';
import CompareGameFilter from '../CompareGameFilter';
import './index.scss';
import { handleDownload } from '@/pages/dashboard/utils/utils';

interface ICompetitorTableProps {
  // 英文标题
  titleEn?: string;
  // 中文标题
  titleCn?: string;
  subTitleEn?: string;
  subTitleCn?: string;
  className?: string;
  style?: React.CSSProperties;
  activeList?: string[];
  checkOptions?: {
    nameEn: string;
    nameCn: string;
    value: string;
    style: React.CSSProperties;
  }[];
  activeSort: string[];
  [key: string]: any;
}

type TColumn = {
  key: string;
  header: React.ReactNode;
  downHeader: string;
  width: number;
  align?: 'left' | 'center' | 'right';
  type?: string;
  className?: string;
  render?: (item: any) => React.ReactNode;
};

export default function CompetitorTable(props: ICompetitorTableProps) {
  const {
    className,
    style,
    activeList,
    checkOptions,
    activeSort,
    data,
    metricConfig,
    showCheck,
  } = props || {};
  const contextObj = useContext(FilterContext);
  const { filterVal } = contextObj || {};
  const isEn = getLocale() === LANGUAGE_EN;
  const { genre_list, selector, compareType, changeGameCallback, gamesInfo } = filterVal || {};
  const { loading, errorMsg, data: apiData } = data?.radarChartData || {};
  const { formatMessage } = useIntl();
  // 获取指标、品类选项数据
  const { metricOptions, genreOptions } = selector || {};

  const { currentGameCn } = useSelector((state: any) => ({
    currentGameCn: state.dashboard?.currentGame?.game_cn_name,
  }), shallowEqual);

  const [visible, setVisible] = useState(false);

  const [columns, setColumns] = useState<TColumn[]>([]);

  const divRef = useRef<HTMLDivElement>(null);

  const [xScrollWidth, setXScrollWidth] = useState(0);

  const tableData = useMemo(() => {
    const {
      dashboard_game_metrics,
      compare_metrics,
      compare_game_metrics_list,
    } = apiData || {};

    // 获取选中指标选项数据
    const metricList = filterVal?.metrics?.map(key => {
      const item = metricOptions?.find((item) => item.metric === key);
      return item || { metric: key, metric_ch: key, metric_en: key };
    });

    const tableData = metricList?.map((item) => {
      const { metric, metric_ch, metric_en } = item;

      // 获取指标信息
      const metricInfo = metricConfig?.[metric] || {};

      const name = getName(isEn, metric_en, metric_ch);
      const value = dataFormat({
        value: dashboard_game_metrics?.metrics?.[metric]?.value,
        formatRule: metricInfo.formatter,
      });
      const top10per = dataFormat({
        value: compare_metrics?.[metric]?.top10per,
        formatRule: metricInfo.formatter,
      });
      const median = dataFormat({
        value: compare_metrics?.[metric]?.median,
        formatRule: metricInfo.formatter,
      });
      const percentile = dashboard_game_metrics?.metrics?.[metric]?.percentile || 0;
      const obj = {
        name,
        value,
        top10per,
        median,
        percentile: dataFormat({ value: percentile * 100, formatRule: 'oneDecimal' }),
      };

      // 竞品游戏
      if (compare_game_metrics_list?.length > 0) {
        compare_game_metrics_list?.forEach((item) => {
          const { game_id, metrics } = item;
          obj[game_id] = dataFormat({
            value: metrics?.[metric]?.value,
            formatRule: metricInfo.formatter,
          });
        });
      }

      return obj;
    });
    return tableData || [];
  }, [apiData, filterVal.metrics, isEn, metricConfig, metricOptions]);

  const gameValues = useMemo(() => {
    let gameList = [...(apiData?.compare_game_metrics_list || [])];
    if (gamesInfo?.[compareType]) {
      gameList = gamesInfo?.[compareType];
    }
    return gameList;
  }, [apiData?.compare_game_metrics_list, compareType, gamesInfo]);

  // 表头联动
  useEffect(() => {
    // 首列
    const columnsList: TColumn[] = [
      {
        key: 'name',
        header: (
          <div onClick={() => setVisible(true)} className='dbd-benchmark-competitor-table-game-selector-container'>
            <div className='dbd-benchmark-competitor-table-game-selector'><SvgIcon type='icon_add_game' /></div>
            {formatMessage({ id: 'benchmark_table_add_sort_games' })}
          </div>
        ),
        downHeader: getName(isEn, 'Metrics', '指标'),
        width: 330,
        render: getMetricRender,
      },
    ];

    if (showCheck) {
      // 固定顺序/联动top10和median
      const topMedialColumns: any[] = [];
      activeSort?.forEach((key) => {
        if (activeList?.includes(key)) {
          const item = checkOptions?.find((item) => item.value === key);
          const { value, nameEn, nameCn, style = {} } = item || {};
          // 品类名称
          const genreItem =
            genreOptions?.find((item: any) => item.code === genre_list?.[0]) || {};
          const { name_ch, name_en } = genreItem;

          topMedialColumns.push({
            key: value,
            align: 'center',
            width: 200,
            header: getTop10MidianHeader(
              getName(isEn, nameEn, nameCn),
              style,
              getName(isEn, name_en, name_ch),
            ),
            downHeader: getName(isEn, nameEn, nameCn),
          });
        }
      });
      columnsList.push(...topMedialColumns);
    }

    // 联动game
    const { dashboard_game_metrics } = apiData || {};

    const { cover, game_name } = dashboard_game_metrics || {};

    const currentName = getName(isEn, game_name, currentGameCn);

    const isPc = data?.dataFrame === 'pc';

    // 本游戏列
    columnsList.push({
      key: 'value',
      className: 'dbd-benchmark-competitor-table-current-game-td',
      width: 400,
      header: getGameHeader(currentName, cover, isPc),
      downHeader: currentName,
      render: (item: any) => currentGameRender(item, isEn),
    });
    // 竞品游戏列
    if (gameValues?.length > 0) {
      gameValues.forEach((item) => {
        const { game_id, game_name, cover } = item;
        columnsList.push({
          key: game_id,
          align: 'center',
          type: 'game',
          width: 200,
          downHeader: game_name,
          header: getGameHeader(game_name, cover, isPc),
        });
      });
    }

    setColumns(columnsList);
  }, [activeList, activeSort, checkOptions, genre_list, genreOptions, isEn, apiData, gameValues, formatMessage, showCheck, currentGameCn, data]);

  const changeGame = (index: number) => {
    const newArr = [...columns];
    [newArr[index], newArr[index + 1]] = [newArr[index + 1], newArr[index]]; // 交换元素
    setColumns(newArr);
    // 更新筛选器顺序
    const keys = newArr.map((item) => item.key);
    const gameList: any[] = [];
    keys.forEach(key => {
      const game = gameValues.find(item => item.game_id === key);
      if (game) {
        gameList.push(game);
      }
    });
    changeGameCallback?.(compareType, gameList);
  };

  const closeGame = (index: number) => {
    const newColumns = [...columns];
    const delColumns = newColumns.splice(index, 1);
    setColumns(newColumns);
    changeGameCallback?.(compareType, gameValues?.filter((item) => item.game_id !== delColumns[0]?.key), true);
  };

  const onConfirm = (value: any[]) => {
    // overall和genre共享一个游戏
    changeGameCallback?.(compareType, value, true);
  };

  // 滚动触发
  useEffect(() => {
    const handleScroll = () => {
      const scrollableDiv = divRef.current;
      if (scrollableDiv) {
        setXScrollWidth(scrollableDiv.scrollLeft);
      }
    };

    const scrollableDiv = divRef.current;
    if (scrollableDiv) {
      scrollableDiv.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollableDiv) {
        scrollableDiv.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const download = () => {
    const tableConfig = {
      option: columns.map(item => ({ key: item.key, header: item.downHeader })),
      downloadData: tableData
    };
    const titleMsg = isEn ? 'Benchmark' : '	行业基准';
    handleDownload(tableConfig, titleMsg);
  };

  return (
    <div
      className={classNames(
        'dbd-benchmark-competitor-table-container',
        { 'dbd-benchmark-competitor-table-scroll': xScrollWidth > 0 },
        className,
      )}
      style={style}
    >
      <i className="dbd-benchmark-competitor-table-down-btn" onClick={download}>
        <SvgIcon type="download" />
      </i>
      {visible && <CompareGameFilter
        visible={visible}
        value={gameValues}
        onConfirm={onConfirm}
        onClose={() => setVisible(false)} />}
      <div className='dbd-benchmark-competitor-table' ref={divRef}>
        <Abnormal
          isLoading={loading}
          isError={!!errorMsg}
          component={
            <table>
              <thead>
                <tr>
                  {columns?.map((item, index) => {
                    const { header, align = 'left', type, width = 'auto' } = item;
                    return (
                      <th
                        key={index}
                        align={align}
                        style={{
                          position: type === 'game' ? 'relative' : undefined,
                          width: `${width}px`,
                        }}
                      >
                        {header}
                        {type === 'game' && index !== columns.length - 1 && (
                          <CommonBubble content={formatMessage({ id: 'benchmark_table_txchange_position' })}>
                            <i className="dbd-benchmark-competitor-table-change-game" onClick={() => changeGame(index)}>
                              <SvgIcon type="exchange-icon" />
                            </i>
                          </CommonBubble>
                        )}
                        {type === 'game' && (
                          <CommonBubble content={formatMessage({ id: 'benchmark_table_remove_this_game' })}>
                            <i className="dbd-benchmark-competitor-table-close-game">
                              <SvgIcon
                                type="clear-icon"
                                onClick={() => closeGame(index)}
                              />
                            </i>
                          </CommonBubble>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {tableData?.map((item, index) => (
                  <tr key={index}>
                    {columns?.map((col, index) => {
                      const { render, key, align, className } = col;
                      return (
                        <td key={index} align={align} className={className}>
                          {(render ? render(item) : item[key]) || '-'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          }

        />
      </div>
    </div>
  );
}
